import React, { useEffect, useState } from 'react';
import { dataService, PVCSnapshotData, CommentaryType } from '@rti/shared';
import { useTheme } from '@/contexts/ThemeContext';
import {
  normalizeRows,
  getChange,
  getSortedDates,
  getAvailableDates,
  getFirstDateWithPrelimCommentary,
  formatDateLabel,
  formatCommentary,
  renderStatCard,
  hasCommentaryData
} from './snapshotUtils';
import { createChart, rsColors, formatLabel, commonChartOptions, regionColors, getAxisColors } from './chartUtils';
import { Chart } from 'chart.js';
import { normalizeSnapshotResponse } from './snapshotDataUtils';
import { trackUserEvent } from '@/utils/userTracking';

interface SnapshotPVCProps {
  onCommentaryTypeChange?: (type: 'preliminary' | 'final') => void;
  onSelectedDateChange?: (date: string) => void;
}

const SnapshotPVC: React.FC<SnapshotPVCProps> = ({ onCommentaryTypeChange, onSelectedDateChange }) => {
  const { isDark } = useTheme();
  const { tickColor, gridColor } = getAxisColors(isDark);
  const [stats, setStats] = useState<any>({});
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [commentaryTexts, setCommentaryTexts] = useState<any>({});
  const [commentaryType, setCommentaryType] = useState<CommentaryType>('preliminary');
  const [chartInstances, setChartInstances] = useState<{ [key: string]: Chart | null }>({});
  const [prelimData, setPrelimData] = useState<PVCSnapshotData[]>([]);
  const [finalData, setFinalData] = useState<PVCSnapshotData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const currentData: PVCSnapshotData[] = commentaryType === 'final' && finalData.length > 0 ? finalData : prelimData;

  const handleCommentaryTypeChange = (type: CommentaryType) => {
    setCommentaryType(type);
    onCommentaryTypeChange?.(type);
  };

  useEffect(() => {
    const loadBothDatasets = async () => {
      try {
        setLoading(true);
        const [prelimResp, finalResp] = await Promise.all([
          dataService.getACCPVCData(),
          dataService.getACCPVCDataFinal()
        ]);
        const prelimArray = normalizeSnapshotResponse<PVCSnapshotData>(prelimResp) || [];
        const finalArray = normalizeSnapshotResponse<PVCSnapshotData>(finalResp) || [];
        setPrelimData(prelimArray);
        setFinalData(finalArray);

        const base = finalArray.length > 0 ? finalArray : prelimArray;
        if (base.length > 0) {
          initializeFromBase(base, finalArray, prelimArray);
        }
      } finally {
        setLoading(false);
      }
    };

    loadBothDatasets();
  }, []);

  const initializeFromBase = (rows: PVCSnapshotData[], finalDataArray: PVCSnapshotData[], prelimDataArray: PVCSnapshotData[]) => {
    const normalizedRows = normalizeRows(rows);
    const sorted = getSortedDates(normalizedRows);
    const available = getAvailableDates(sorted, 25, prelimDataArray);

    setAvailableDates(available);

    const firstDateWithCommentary = getFirstDateWithPrelimCommentary(prelimDataArray, 25);
    const defaultDate = firstDateWithCommentary || available[0] || sorted[0].date;

    const defaultRow = finalDataArray.find(f => f.date === defaultDate);
    const shouldUseFinal = defaultRow && hasCommentaryData(defaultRow);

    setCommentaryType(shouldUseFinal ? 'final' : 'preliminary');
    onCommentaryTypeChange?.(shouldUseFinal ? 'final' : 'preliminary');

    setSelectedDate(defaultDate);
    onSelectedDateChange?.(defaultDate);
    updateDataForSelectedDate(defaultDate);
  };

  useEffect(() => {
    if (!selectedDate || currentData.length === 0) return;
    updateDataForSelectedDate(selectedDate);
  }, [selectedDate, commentaryType, prelimData, finalData, isDark]);

  const destroyCharts = () => {
    Object.values(chartInstances).forEach(chart => {
      if (chart) {
        try {
          chart.destroy();
        } catch {}
      }
    });
    setChartInstances({});
  };

  const createNewCharts = (rows: PVCSnapshotData[]) => {
    const sorted = [...rows].filter(r => r.date).sort((a, b) => new Date(a.date) > new Date(b.date) ? 1 : -1);
    if (!sorted.length) return;

    const labels = sorted.map(d => formatLabel(d.date));

    const cumulativeCanvas = document.getElementById('pvcCumulativeChart') as HTMLCanvasElement | null;
    const productionCanvas = document.getElementById('pvcProductionChart') as HTMLCanvasElement | null;
    const exportsCanvas = document.getElementById('pvcGlobalExport') as HTMLCanvasElement | null;
    const domesticCanvas = document.getElementById('DomesticSales') as HTMLCanvasElement | null;

    const inventoryCumulative: number[] = [];
    sorted.reduce((prev, cur) => {
      const change = Number(cur.inventory_change_monthly || 0) / 1000;
      const next = prev + change;
      inventoryCumulative.push(next);
      return next;
    }, 0);

    const cumulativeChart = cumulativeCanvas
      ? createChart(cumulativeCanvas, {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: 'PVC Cumulative Draws/Gains',
                data: inventoryCumulative,
                borderColor: rsColors.purple,
                backgroundColor: 'rgba(133,76,255,0.1)',
                fill: false,
                tension: 0.2,
                pointRadius: 0
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { display: false },
              datalabels: { display: false }
            },
            scales: {
              x: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: {
                ticks: { color: tickColor },
                grid: { color: gridColor, lineWidth: (ctx: any) => (ctx.tick.value === 0 ? 2 : 0.3) },
                grace: '2%'
              }
            }
          }
        })
      : null;

    const domesticDemand = sorted.map(d => +(d.domestic_demand || 0));
    const exportsTotal = sorted.map(d => +(d.exports_total || 0));
    const capacity = sorted.map(d => +(d.capacity || 0));
    const production = sorted.map(d => +(d.domestic_production || 0));
    const fillColor = isDark ? 'rgba(204,210,195,.1)' : 'rgba(100,100,100,.4)';

    const productionChart = productionCanvas
      ? createChart(productionCanvas, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                type: 'line' as const,
                label: 'Capacity',
                data: capacity,
                borderColor: '#888888',
                backgroundColor: fillColor,
                fill: true,
                order: 3,
                yAxisID: 'y',
                pointRadius: 0
              },
              {
                type: 'line' as const,
                label: 'Production',
                data: production,
                borderColor: '#E85b4c',
                fill: false,
                order: 1,
                yAxisID: 'y',
                pointRadius: 0
              },
              {
                type: 'bar' as const,
                label: 'Domestic Demand',
                data: domesticDemand,
                backgroundColor: '#42b200',
                stack: 'stack1',
                order: 2,
                yAxisID: 'y'
              },
              {
                type: 'bar' as const,
                label: 'Exports Total',
                data: exportsTotal,
                backgroundColor: '#3838FF',
                stack: 'stack1',
                order: 2,
                yAxisID: 'y'
              }
            ]
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: { position: 'top' as const, labels: { color: tickColor } },
              datalabels: { display: false }
            },
            scales: {
              x: { stacked: false, ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } }
            }
          }
        })
      : null;

    const exportKeys = Object.keys(sorted[0] || {}).filter(k => k.startsWith('export_') && k !== 'export_rates');
    const exportDatasets = exportKeys.map((key, idx) => {
      const friendlyLabel = key
        .replace(/^export_/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());

      return {
        label: friendlyLabel,
        data: sorted.map(d => {
          const date = new Date(d.date);
          const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          return (+(d[key as keyof PVCSnapshotData] || 0)) * daysInMonth;
        }),
        backgroundColor: regionColors[idx % regionColors.length],
        stack: 'region',
        yAxisID: 'y'
      };
    });

    const exportsChart = exportsCanvas
      ? createChart(exportsCanvas, {
          type: 'bar',
          data: {
            labels,
            datasets: exportDatasets
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: {
                position: 'top' as const,
                labels: { usePointStyle: true, pointStyle: 'circle', padding: 20, color: tickColor }
              },
              datalabels: { display: false }
            },
            scales: {
              x: { stacked: true, ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { stacked: true, ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } }
            }
          }
        })
      : null;

    const segmentKeys = Object.keys(sorted[0] || {}).filter(k => k.startsWith('segment_'));
    const segmentDatasets = segmentKeys.map((key, idx) => {
      let label = key.replace(/^segment_/, '').replace(/_/g, ' ');
      const dashCount = (label.match(/-/g) || []).length;
      if (dashCount === 1) {
        label = label.replace('-', ' (') + ')';
      } else if (dashCount > 1) {
        const first = label.indexOf('-');
        const last = label.lastIndexOf('-');
        label = label.slice(0, first) + ' (' + label.slice(first + 1, last) + ')' + label.slice(last + 1);
      }

      return {
        label: label.replace(/\b\w/g, c => c.toUpperCase()),
        data: sorted.map(d => {
          const date = new Date(d.date);
          const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
          return ((+(d[key as keyof PVCSnapshotData] || 0)) * daysInMonth) / 1000;
        }),
        backgroundColor: regionColors[idx % regionColors.length],
        stack: 'segments',
        yAxisID: 'y'
      };
    });

    const domesticChart = domesticCanvas
      ? createChart(domesticCanvas, {
          type: 'bar',
          data: {
            labels,
            datasets: segmentDatasets
          },
          options: {
            ...commonChartOptions,
            plugins: {
              legend: {
                position: 'top' as const,
                labels: { usePointStyle: true, pointStyle: 'circle', padding: 20, color: tickColor }
              },
              datalabels: { display: false }
            },
            scales: {
              x: { stacked: true, ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } },
              y: { stacked: true, ticks: { color: tickColor }, grid: { color: gridColor, lineWidth: 0.3 } }
            }
          }
        })
      : null;

    setChartInstances({
      cumulative: cumulativeChart,
      production: productionChart,
      exports: exportsChart,
      domestic: domesticChart
    });
  };

  const renderCharts = (rows: PVCSnapshotData[]) => {
    destroyCharts();
    setTimeout(() => {
      createNewCharts(rows);
    }, 50);
  };

  const updateDataForSelectedDate = (date: string) => {
    const allSorted = [...currentData].sort((a, b) => new Date(b.date) > new Date(a.date) ? 1 : -1);
    const currentRow = allSorted.find(r => r.date === date);
    const currentIndex = allSorted.findIndex(r => r.date === date);
    const prevRow = currentIndex >= 0 ? allSorted[currentIndex + 1] : undefined;
    if (!currentRow) return;

    const currentDateObj = new Date(currentRow.date);
    const lastYearRow = allSorted.find(r => {
      const d = new Date(r.date);
      return (
        d.getFullYear() === currentDateObj.getFullYear() - 1 &&
        d.getMonth() === currentDateObj.getMonth()
      );
    });

    const pvcStats = {
      pvcInventory: {
        value: Number(((currentRow.inventory_monthly || 0) / 1000).toFixed(2)),
        change: `${Number(((currentRow.inventory_change_monthly || 0) / 1000).toFixed(2)) > 0 ? "+" : ""}${Number(((currentRow.inventory_change_monthly || 0) / 1000).toFixed(2)).toString()}`
      },
      pvcDemandRate: {
        value: Math.round((parseFloat(currentRow.demand_rates as string) || 0) * 100),
        change: prevRow
          ? getChange(
              (parseFloat(currentRow.demand_rates as string) || 0) * 100,
              (parseFloat(prevRow.demand_rates as string) || 0) * 100,
              true
            )
          : ''
      },
      pvcExportRate: {
        value: Math.round((parseFloat(currentRow.export_rates as string) || 0) * 100),
        change: prevRow
          ? getChange(
              (parseFloat(currentRow.export_rates as string) || 0) * 100,
              (parseFloat(prevRow.export_rates as string) || 0) * 100,
              true
            )
          : ''
      },
      pvcOperatingRate: {
        value: Math.round((parseFloat(currentRow.operating_rates as string) || 0) * 100),
        change: prevRow
          ? getChange(
              (parseFloat(currentRow.operating_rates as string) || 0) * 100,
              (parseFloat(prevRow.operating_rates as string) || 0) * 100,
              true
            )
          : ''
      },
      pvcDays: {
        value: Math.round(Number(currentRow.days_of_inventory ?? 0)),
        change: prevRow
          ? getChange(
              Number(currentRow.days_of_inventory ?? 0),
              Number(prevRow.days_of_inventory ?? 0)
            )
          : ''
      },
      pvcInventoryChange: {
        value: Math.round(
          currentRow.inventory_change_monthly || currentRow.inventory_change || 0
        ).toLocaleString(),
        change: ''
      },
      pvcDomesticDemand: {
        value: Math.round(currentRow.domestic_demand || 0).toLocaleString(),
        change: prevRow
          ? getChange(currentRow.domestic_demand, prevRow.domestic_demand)
          : ''
      },
      pvcDomesticProduction: {
        value: Math.round(currentRow.domestic_production || 0).toLocaleString(),
        change: prevRow
          ? getChange(currentRow.domestic_production, prevRow.domestic_production)
          : ''
      },
      pvcTotalSales: {
        value: Math.round(currentRow.total_sales_and_captive_use || 0).toLocaleString(),
        change: prevRow
          ? getChange(
              currentRow.total_sales_and_captive_use,
              prevRow.total_sales_and_captive_use
            )
          : ''
      },
      pvcCapacity: {
        value: Math.round(currentRow.capacity || 0).toLocaleString(),
        change: prevRow ? getChange(currentRow.capacity, prevRow.capacity) : ''
      },
      annualInventory: lastYearRow
        ? getChange(
            (currentRow.inventory_monthly || 0) / 1000,
            (lastYearRow.inventory_monthly || 0) / 1000,
          false,
        1,
        2
          )
        : 'Flat',
      annualDemandRate: lastYearRow
        ? getChange(
            (parseFloat(currentRow.demand_rates as string) || 0) * 100,
            (parseFloat(lastYearRow.demand_rates as string) || 0) * 100,
            true
          )
        : 'Flat',
      annualExportRate: lastYearRow
        ? getChange(
            (parseFloat(currentRow.export_rates as string) || 0) * 100,
            (parseFloat(lastYearRow.export_rates as string) || 0) * 100,
            true
          )
        : 'Flat',
      annualOperatingRate: lastYearRow
        ? getChange(
            (parseFloat(currentRow.operating_rates as string) || 0) * 100,
            (parseFloat(lastYearRow.operating_rates as string) || 0) * 100,
            true
          )
        : 'Flat'
    };

    setStats(pvcStats);
    setCommentaryTexts({
      cumulativeGains: currentRow.pvc_inv_cum_gains_draws || '',
      productionSales: currentRow.pvc_prod_sales || '',
      globalExports: currentRow.pvc_exports || '',
      domesticSegments: currentRow.pvc_domestic_sales || ''
    });

    const latestDate = new Date(date);
    const series = allSorted.filter(d => {
      const currentDate = new Date(d.date);
      const monthsDiff =
        (latestDate.getFullYear() - currentDate.getFullYear()) * 12 +
        (latestDate.getMonth() - currentDate.getMonth());
      return monthsDiff <= 12 && monthsDiff >= 0;
    });
    if (series.length) renderCharts(series);
  };

  const normalizeAnnual = (value: string | number | undefined) => {
    if (!value) return 'Flat';
    const str = String(value).trim();
    if (!str) return 'Flat';
    return str;
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedDate(value);
    onSelectedDateChange?.(value);

    const finalRowForNewDate = finalData.find(f => f.date === value);
    const hasFinalCommentaryForNewDate = finalRowForNewDate && hasCommentaryData(finalRowForNewDate);

    if (commentaryType === 'final' && !hasFinalCommentaryForNewDate) {
      setCommentaryType('preliminary');
      onCommentaryTypeChange?.('preliminary');
    }

    trackUserEvent('monthly_pvc_snapshot_date_change', 'PVC Snapshot Date Changed', {
      date: value,
      type: commentaryType
    });
  };

  const handleCommentaryTypeSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as CommentaryType;
    handleCommentaryTypeChange(value);
    trackUserEvent('monthly_pvc_snapshot_type_change', 'PVC Snapshot Type Changed', {
      date: selectedDate,
      type: value
    });
  };

  const selectedDateHasFinalCommentary = () => {
    const finalRow = finalData.find(f => f.date === selectedDate);
    if (!finalRow) return false;

    const selectedDateObj = new Date(selectedDate);
    const now = new Date();
    const monthsDiff = (now.getFullYear() - selectedDateObj.getFullYear()) * 12 + (now.getMonth() - selectedDateObj.getMonth());

    if (monthsDiff >= 2) {
      return true;
    }

    return hasCommentaryData(finalRow);
  };

  if (loading) {
    return (
      <div className="text-3xl flex text-rs-off-white items-center justify-center h-[500px] w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-5">
        <div className="flex items-center gap-4">
          <select
            value={selectedDate}
            onChange={handleDateChange}
            className="border-rs-jet border-2 p-4 rounded-lg"
          >
            {availableDates.map(date => (
              <option key={date} value={date}>
                {formatDateLabel(date)}
              </option>
            ))}
          </select>
          <select
            value={commentaryType}
            onChange={handleCommentaryTypeSelect}
            className="border-rs-jet border-2 p-4 rounded-lg"
          >
            <option value="preliminary">Preliminary</option>
            {selectedDateHasFinalCommentary() && <option value="final">Final</option>}
          </select>
        </div>
      </div>

      {}
      <h3 className="text-xl text-rs-off-white font-bold mb-3">PVC Market Statistics</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {renderStatCard(
          'Calc. Inventory (mm-lbs)',
          stats.pvcInventory?.value || 0,
          stats.pvcInventory?.change || '0',
          '',
          '',
          normalizeAnnual(stats.annualInventory)
        )}
        {renderStatCard(
          'Demand Rate',
          `${stats.pvcDemandRate?.value || 0}%`,
          stats.pvcDemandRate?.change || '0',
          '',
          '',
          normalizeAnnual(stats.annualDemandRate)
        )}
        {renderStatCard(
          'Export Rate',
          `${stats.pvcExportRate?.value || 0}%`,
          stats.pvcExportRate?.change || '0',
          '',
          '',
          normalizeAnnual(stats.annualExportRate)
        )}
        {renderStatCard(
          'Operating Rate',
          `${stats.pvcOperatingRate?.value || 0}%`,
          stats.pvcOperatingRate?.change || '0',
          '',
          '',
          normalizeAnnual(stats.annualOperatingRate)
        )}
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">PVC Cumulative Draws/Gains</h4>
          <div className="h-[380px]">
            <canvas id="pvcCumulativeChart" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-4 mt-3">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.cumulativeGains) }}
            />
          </div>
        </div>

        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">PVC Production & Sales</h4>
          <div className="h-[380px]">
            <canvas id="pvcProductionChart" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-4 mt-3">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.productionSales) }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">Global Exports</h4>
          <div className="h-[380px]">
            <canvas id="pvcGlobalExport" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-4 mt-3">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.globalExports) }}
            />
          </div>
        </div>

        <div className="bg-rs-off-black rounded-lg p-5">
          <h4 className="text-center mb-3 font-medium">Domestic Market Segments</h4>
          <div className="h-[420px] pt-4">
            <canvas id="DomesticSales" className="w-full h-full"></canvas>
          </div>
          <div className="bg-rs-off-black rounded-lg p-4 mt-3">
            <p
              className="whitespace-pre-line text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatCommentary(commentaryTexts.domesticSegments) }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SnapshotPVC;
